// EventBridge Scheduler expressions — cron(), rate() and at() — parsed, described
// and evaluated in the browser, so the scheduler page can describe any schedule and
// lay its runs out on a calendar from the inventory it already has.
//
// EventBridge cron has six fields: minutes, hours, day-of-month, month, day-of-week
// and year. Day-of-week counts 1 = SUN … 7 = SAT, and one of the two day fields must
// be "?". Besides lists, ranges and steps, day-of-month accepts L (last day), LW and
// nW (nearest weekday), and day-of-week accepts nL (last <day> of the month) and n#k
// (k-th <day> of the month).

export const DISPLAY_TIMEZONE = "America/New_York";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const FIELDS = [
  { key: "minute", min: 0, max: 59 },
  { key: "hour", min: 0, max: 23 },
  { key: "dayOfMonth", min: 1, max: 31 },
  { key: "month", min: 1, max: 12, names: MONTH_NAMES },
  { key: "dayOfWeek", min: 1, max: 7, names: DAY_NAMES },
  { key: "year", min: 1970, max: 2199 },
];

// Above this many runs a day, a schedule is summarised instead of listing each run.
const MAX_RUNS_LISTED_PER_DAY = 48;
const RATE_UNIT_MS = { minute: 60000, hour: 3600000, day: 86400000 };

function isoMs(value) {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

function parseToken(token, field) {
  let value;
  if (/^\d+$/.test(token)) {
    value = Number(token);
  } else {
    const index = (field.names || []).findIndex(
      (name) => name.toUpperCase() === token
    );
    if (index < 0) throw new Error(`Unknown value ${token}`);
    value = index + 1;
  }
  if (value < field.min || value > field.max) {
    throw new Error(`${token} is out of range`);
  }
  return value;
}

function parseField(text, field) {
  const result = {
    text,
    any: false,
    question: false,
    values: new Set(),
    parts: [],
    specials: [],
  };
  const size = field.max - field.min + 1;
  for (const raw of text.split(",")) {
    const part = raw.trim().toUpperCase();
    if (!part) throw new Error("Empty field part");
    if (part === "?") {
      if (field.key !== "dayOfMonth" && field.key !== "dayOfWeek") {
        throw new Error("? is only allowed in the day fields");
      }
      result.question = true;
      continue;
    }
    if (field.key === "dayOfMonth") {
      if (part === "L" || part === "LW") {
        result.specials.push({ type: part === "L" ? "last" : "lastWeekday" });
        continue;
      }
      const weekday = /^(\d+)W$/.exec(part);
      if (weekday) {
        result.specials.push({ type: "nearestWeekday", day: parseToken(weekday[1], field) });
        continue;
      }
    }
    if (field.key === "dayOfWeek" && part !== "L") {
      const last = /^([A-Z0-9]+)L$/.exec(part);
      if (last) {
        result.specials.push({ type: "lastOfMonth", weekday: parseToken(last[1], field) });
        continue;
      }
      const nth = /^([A-Z0-9]+)#([1-5])$/.exec(part);
      if (nth) {
        result.specials.push({
          type: "nthOfMonth",
          weekday: parseToken(nth[1], field),
          n: Number(nth[2]),
        });
        continue;
      }
    }
    // "L" alone in day-of-week means the last day of the week, Saturday.
    const match = /^(\*|[A-Z0-9]+)(?:-([A-Z0-9]+))?(?:\/(\d+))?$/.exec(
      field.key === "dayOfWeek" && part === "L" ? "SAT" : part
    );
    if (!match) throw new Error(`Cannot read ${part}`);
    const step = match[3] ? Number(match[3]) : 1;
    if (step < 1) throw new Error("Step must be at least 1");
    const star = match[1] === "*";
    if (star && match[2]) throw new Error("* cannot start a range");
    const start = star ? field.min : parseToken(match[1], field);
    const end = star
      ? field.max
      : match[2]
      ? parseToken(match[2], field)
      : match[3]
      ? field.max
      : start;
    // Ranges may wrap, e.g. FRI-MON or NOV-FEB.
    const span = end >= start ? end - start : end - start + size;
    for (let offset = 0; offset <= span; offset += step) {
      result.values.add(field.min + ((start - field.min + offset) % size));
    }
    const type = match[3] ? "step" : star ? "any" : match[2] ? "range" : "value";
    if (type === "any") result.any = true;
    result.parts.push({ type, start, end, step, star });
  }
  if (!result.question && !result.values.size && !result.specials.length) {
    throw new Error("Field matches nothing");
  }
  return result;
}

export function parseScheduleExpression(expression) {
  const text = String(expression || "").trim();
  const cron = /^cron\((.*)\)$/i.exec(text);
  if (cron) {
    const tokens = cron[1].trim().split(/\s+/);
    if (tokens.length !== FIELDS.length) return null;
    try {
      const fields = {};
      FIELDS.forEach((field, index) => {
        fields[field.key] = parseField(tokens[index], field);
      });
      return { kind: "cron", fields };
    } catch (error) {
      return null;
    }
  }
  const rate = /^rate\(\s*(\d+)\s+(minute|minutes|hour|hours|day|days)\s*\)$/i.exec(text);
  if (rate) {
    return {
      kind: "rate",
      value: Number(rate[1]),
      unit: rate[2].toLowerCase().replace(/s$/, ""),
    };
  }
  const at = /^at\(\s*(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?\s*\)$/i.exec(text);
  if (at) {
    const [year, month, day, hour, minute] = at.slice(1).map(Number);
    return { kind: "at", year, month, day, hour, minute };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Calendar arithmetic on civil dates (no local-timezone surprises)

function pad(value) {
  return String(value).padStart(2, "0");
}

export function dayKey(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function parseDayKey(key) {
  const [year, month, day] = key.split("-").map(Number);
  return { year, month, day };
}

export function addDays(key, days) {
  const { year, month, day } = parseDayKey(key);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return dayKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

// EventBridge numbering: 1 = Sunday … 7 = Saturday.
function weekdayOf(year, month, day) {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 1;
}

function nearestWeekday(year, month, target) {
  const last = daysInMonth(year, month);
  const day = Math.min(target, last);
  const weekday = weekdayOf(year, month, day);
  if (weekday === 7) return day > 1 ? day - 1 : day + 2;
  if (weekday === 1) return day < last ? day + 1 : day - 2;
  return day;
}

function matchesDayOfMonth(field, year, month, day) {
  if (field.values.has(day)) return true;
  const last = daysInMonth(year, month);
  return field.specials.some((special) => {
    if (special.type === "last") return day === last;
    if (special.type === "lastWeekday") return day === nearestWeekday(year, month, last);
    return day === nearestWeekday(year, month, special.day);
  });
}

function matchesDayOfWeek(field, year, month, day) {
  const weekday = weekdayOf(year, month, day);
  if (field.values.has(weekday)) return true;
  return field.specials.some((special) => {
    if (special.weekday !== weekday) return false;
    if (special.type === "lastOfMonth") return day + 7 > daysInMonth(year, month);
    return Math.ceil(day / 7) === special.n;
  });
}

function matchesDate(fields, year, month, day) {
  if (!fields.year.values.has(year) || !fields.month.values.has(month)) return false;
  const dom = fields.dayOfMonth;
  const dow = fields.dayOfWeek;
  if (dom.question && dow.question) return true;
  if (dom.question) return matchesDayOfWeek(dow, year, month, day);
  if (dow.question) return matchesDayOfMonth(dom, year, month, day);
  return matchesDayOfMonth(dom, year, month, day) && matchesDayOfWeek(dow, year, month, day);
}

function timesOfDay(fields) {
  const hours = [...fields.hour.values].sort((a, b) => a - b);
  const minutes = [...fields.minute.values].sort((a, b) => a - b);
  return hours.flatMap((hour) => minutes.map((minute) => ({ hour, minute })));
}

// ---------------------------------------------------------------------------
// Time zones via Intl, so a schedule in any IANA zone lands on the right ET day

const formatters = new Map();

function wallClock(timezone, utcMs) {
  let formatter = formatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    });
    formatters.set(timezone, formatter);
  }
  const parts = {};
  for (const part of formatter.formatToParts(new Date(utcMs))) {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  }
  return parts;
}

function safeTimezone(timezone) {
  try {
    wallClock(timezone || "UTC", 0);
    return timezone || "UTC";
  } catch (error) {
    return "UTC";
  }
}

function zonedToUtc(year, month, day, hour, minute, timezone) {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  let utc = wall;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const seen = wallClock(timezone, utc);
    utc += wall - Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute);
  }
  return utc;
}

export function todayIn(timezone = DISPLAY_TIMEZONE, now = Date.now()) {
  const clock = wallClock(safeTimezone(timezone), now);
  return dayKey(clock.year, clock.month, clock.day);
}

// Runs of one schedule between two ET days (inclusive), keyed by ET day:
// Map<"YYYY-MM-DD", [{ minutes, utcMs, count }]>, where `minutes` is the ET minute
// of the day. A day with more runs than MAX_RUNS_LISTED_PER_DAY yields one entry
// for the first run with `count` set to the day's total.
//
// `bounds` carries the schedule's ISO `startDate` / `endDate` (no runs outside
// them; at() ignores both) and `createdAt`. A rate() schedule counts from its start date, or from
// creation when it has none; without either it returns null.
export function occurrencesByDay(expression, timezone, fromDay, toDay, bounds = {}) {
  const parsed = parseScheduleExpression(expression);
  if (!parsed) return null;
  const startMs = isoMs(bounds.startDate);
  const endMs = isoMs(bounds.endDate);
  const zone = safeTimezone(timezone);
  const byDay = new Map();
  const add = (utcMs, count = 1, bounded = true) => {
    if (bounded && startMs !== null && utcMs < startMs) return;
    if (bounded && endMs !== null && utcMs > endMs) return;
    const local = wallClock(DISPLAY_TIMEZONE, utcMs);
    const key = dayKey(local.year, local.month, local.day);
    if (key < fromDay || key > toDay) return;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push({ minutes: local.hour * 60 + local.minute, utcMs, count });
  };

  if (parsed.kind === "at") {
    const { year, month, day, hour, minute } = parsed;
    // One-time schedules ignore StartDate and EndDate.
    add(zonedToUtc(year, month, day, hour, minute, zone), 1, false);
    return byDay;
  }

  if (parsed.kind === "rate") {
    const anchor = startMs ?? isoMs(bounds.createdAt);
    if (anchor === null) return null;
    // Rate units are fixed durations: "days" are 24 hours, whatever the clock does.
    const step = parsed.value * RATE_UNIT_MS[parsed.unit];
    // A day either side of the ET range, as for cron below.
    const { year: fromYear, month: fromMonth, day: fromDate } = parseDayKey(addDays(fromDay, -1));
    const { year: toYear, month: toMonth, day: toDate } = parseDayKey(addDays(toDay, 2));
    const rangeStart = Date.UTC(fromYear, fromMonth - 1, fromDate);
    const rangeEnd = Date.UTC(toYear, toMonth - 1, toDate);
    let utcMs = anchor + Math.max(0, Math.ceil((rangeStart - anchor) / step)) * step;
    const perDay = new Map();
    for (; utcMs < rangeEnd; utcMs += step) {
      if (endMs !== null && utcMs > endMs) break;
      const local = wallClock(DISPLAY_TIMEZONE, utcMs);
      const key = dayKey(local.year, local.month, local.day);
      if (!perDay.has(key)) perDay.set(key, []);
      perDay.get(key).push(utcMs);
    }
    for (const runs of perDay.values()) {
      if (runs.length > MAX_RUNS_LISTED_PER_DAY) add(runs[0], runs.length);
      else runs.forEach((run) => add(run));
    }
    return byDay;
  }

  const times = timesOfDay(parsed.fields);
  // A day either side covers schedules whose zone shifts runs across midnight ET.
  for (let key = addDays(fromDay, -1); key <= addDays(toDay, 1); key = addDays(key, 1)) {
    const { year, month, day } = parseDayKey(key);
    if (!matchesDate(parsed.fields, year, month, day)) continue;
    if (times.length > MAX_RUNS_LISTED_PER_DAY) {
      const first = times[0];
      add(zonedToUtc(year, month, day, first.hour, first.minute, zone), times.length);
      continue;
    }
    for (const { hour, minute } of times) {
      add(zonedToUtc(year, month, day, hour, minute, zone));
    }
  }
  for (const list of byDay.values()) list.sort((a, b) => a.minutes - b.minutes);
  return byDay;
}

// The storefront date a run audits, mirroring the zenyt-{env}-agent-launch state
// machine: a fixed `site_date`, or the calendar date `site_date_offset_days` after
// the run starts, read at UTC-5. Null when the run audits the live site.
export function auditDateForRun(input, utcMs) {
  if (input?.site_date) return String(input.site_date).slice(0, 10);
  const offset = input?.site_date_offset_days;
  if (typeof offset !== "number" || !Number.isInteger(offset)) return null;
  const date = new Date(utcMs + offset * 86400000 - 5 * 3600000);
  return dayKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

// ---------------------------------------------------------------------------
// Descriptions

function ordinal(value) {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = value % 100;
  return `${value}${suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]}`;
}

export function clockLabel(hour, minute) {
  return `${hour % 12 || 12}:${pad(minute)} ${hour < 12 ? "AM" : "PM"}`;
}

export function clockLabelFromMinutes(minutes) {
  return clockLabel(Math.floor(minutes / 60), minutes % 60);
}

function hourLabel(hour) {
  return `${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`;
}

function joinWithAnd(items) {
  if (items.length < 2) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function describeParts(field, label) {
  return field.parts.map((part) => {
    if (part.type === "value") return label(part.start);
    if (part.type === "range") return `${label(part.start)}–${label(part.end)}`;
    if (part.type === "step") {
      return part.star
        ? `every ${part.step}`
        : `every ${part.step} from ${label(part.start)}`;
    }
    return label(part.start);
  });
}

// Day-of-week values as names, folding three or more consecutive days into a range.
function describeWeekdays(field) {
  const plain = field.parts.every((part) => part.type === "value" || part.type === "range");
  const labels = [];
  if (plain) {
    const values = [...field.values].sort((a, b) => a - b);
    for (let index = 0; index < values.length; ) {
      let end = index;
      while (end + 1 < values.length && values[end + 1] === values[end] + 1) end += 1;
      if (end - index >= 2) {
        labels.push(`${DAY_NAMES[values[index] - 1]}–${DAY_NAMES[values[end] - 1]}`);
      } else {
        for (let item = index; item <= end; item += 1) labels.push(DAY_NAMES[values[item] - 1]);
      }
      index = end + 1;
    }
  } else {
    labels.push(...describeParts(field, (value) => DAY_NAMES[value - 1]));
  }
  for (const special of field.specials) {
    const day = DAY_NAMES[special.weekday - 1];
    labels.push(special.type === "lastOfMonth" ? `last ${day}` : `${ordinal(special.n)} ${day}`);
  }
  const text = labels.join(", ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function describeDayOfMonth(field) {
  const labels = describeParts(field, ordinal).map((label) => `the ${label}`);
  for (const special of field.specials) {
    if (special.type === "last") labels.push("the last day");
    else if (special.type === "lastWeekday") labels.push("the last weekday");
    else labels.push(`the weekday nearest the ${ordinal(special.day)}`);
  }
  return joinWithAnd(labels);
}

// { text, weekly }: weekly descriptions ("Tue, Thu", "Daily") read naturally
// straight before a time; dated ones ("Oct 30, 2026") take "at".
function describeDays({ dayOfMonth, month, dayOfWeek, year }) {
  const yearText = year.any ? "" : describeParts(year, String).join(", ");
  const monthText = month.any
    ? ""
    : describeParts(month, (value) => MONTH_NAMES[value - 1]).join(", ");

  const weekdayDriven = dayOfMonth.question || (dayOfMonth.any && !dayOfWeek.question);
  if (weekdayDriven) {
    const days = dayOfWeek.any || dayOfWeek.question ? "Daily" : describeWeekdays(dayOfWeek);
    const scope = [monthText && `in ${monthText}`, yearText].filter(Boolean).join(" ");
    return { text: scope ? `${days} ${scope}` : days, weekly: !scope };
  }

  if (dayOfMonth.any) {
    if (!monthText && !yearText) return { text: "Daily", weekly: true };
    return { text: `Daily in ${[monthText, yearText].filter(Boolean).join(" ")}`, weekly: false };
  }

  const singleMonth = month.parts.length === 1 && month.parts[0].type === "value";
  const plainDays =
    !dayOfMonth.specials.length &&
    dayOfMonth.parts.every((part) => part.type === "value" || part.type === "range");
  if (singleMonth && plainDays) {
    const days = joinWithAnd(
      dayOfMonth.parts.map((part) =>
        part.type === "range" ? `${part.start}–${part.end}` : String(part.start)
      )
    );
    const date = `${MONTH_NAMES[month.parts[0].start - 1]} ${days}`;
    const dated = yearText ? `${date}, ${yearText}` : `${date} every year`;
    const oneDay = dayOfMonth.parts.length === 1 && dayOfMonth.parts[0].type === "value";
    return { text: oneDay ? dated : `Daily ${dated}`, weekly: false };
  }

  const days = describeDayOfMonth(dayOfMonth);
  const base = monthText ? `On ${days} of ${monthText}` : `Monthly on ${days}`;
  return { text: yearText ? `${base} in ${yearText}` : base, weekly: false };
}

// { text, kind }: "times" lists clock times; "interval" describes a repeat.
function describeTimes({ minute, hour }) {
  const times = timesOfDay({ minute, hour });
  const stepped = [...minute.parts, ...hour.parts].some((part) => part.type === "step");
  if (times.length <= 4 && !stepped) {
    return {
      kind: "times",
      text: times.map((time) => clockLabel(time.hour, time.minute)).join(", "),
    };
  }

  const singleStep = (field) =>
    field.parts.length === 1 && field.parts[0].type === "step" ? field.parts[0] : null;
  const hourRange = hour.any
    ? ""
    : describeParts(hour, hourLabel).join(", ");

  let every;
  const minuteStep = singleStep(minute);
  if (minute.any) {
    every = "every minute";
  } else if (minuteStep) {
    every = `every ${minuteStep.step} minutes${
      minuteStep.start ? ` from :${pad(minuteStep.start)}` : ""
    }`;
  } else if (minute.values.size === 1) {
    const at = `:${pad([...minute.values][0])}`;
    const hourStep = singleStep(hour);
    if (hour.any) return { kind: "interval", text: `every hour at ${at}` };
    if (hourStep && hourStep.star) {
      return { kind: "interval", text: `every ${hourStep.step} hours at ${at}` };
    }
    every = `every hour at ${at}`;
  } else {
    every = `${times.length} times a day`;
  }
  return { kind: "interval", text: hourRange ? `${every}, ${hourRange}` : every };
}

function timezoneLabel(timezone) {
  if (timezone === "America/New_York") return "ET";
  return timezone || "UTC";
}

export function describeSchedule(expression, timezone) {
  const parsed = parseScheduleExpression(expression);
  if (!parsed) return expression || "Schedule unavailable";
  const zone = timezoneLabel(timezone);
  if (parsed.kind === "rate") {
    return parsed.value === 1
      ? `Every ${parsed.unit}`
      : `Every ${parsed.value} ${parsed.unit}s`;
  }
  if (parsed.kind === "at") {
    const { year, month, day, hour, minute } = parsed;
    return `Once, ${MONTH_NAMES[month - 1]} ${day}, ${year} at ${clockLabel(hour, minute)} ${zone}`;
  }
  const days = describeDays(parsed.fields);
  const times = describeTimes(parsed.fields);
  if (times.kind === "times") {
    return `${days.text}${days.weekly ? " " : " at "}${times.text} ${zone}`;
  }
  return `${days.text}, ${times.text} (${zone})`;
}

// The earliest time of day a schedule runs, in minutes, for ordering groups.
export function firstTimeOfDay(expression) {
  const parsed = parseScheduleExpression(expression);
  if (!parsed || parsed.kind === "rate") return Infinity;
  if (parsed.kind === "at") return parsed.hour * 60 + parsed.minute;
  const [first] = timesOfDay(parsed.fields);
  return first ? first.hour * 60 + first.minute : Infinity;
}
